import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .alt-page {
    font-family: 'Inter', system-ui, sans-serif;
    color: #e2e8f0;
    background: #060a14;
    min-height: 100vh;
    padding: 0;
    overflow-x: hidden;
  }

  .alt-hero {
    padding: 120px 24px 80px;
    text-align: center;
    background: linear-gradient(180deg, rgba(99,102,241,0.08) 0%, rgba(6,10,20,0) 100%);
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
  }

  .alt-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(99,102,241,0.1);
    color: #a5b4fc;
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 100px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 24px;
  }

  .alt-title {
    font-size: 4rem;
    font-weight: 900;
    color: #f8fafc;
    letter-spacing: -0.04em;
    line-height: 1.1;
    margin: 0 0 24px;
  }

  .alt-title-highlight {
    background: linear-gradient(135deg, #a5b4fc, #818cf8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .alt-subtitle {
    font-size: 1.25rem;
    color: #94a3b8;
    max-width: 700px;
    margin: 0 auto 40px;
    line-height: 1.6;
  }

  .alt-cta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    background: #4f46e5;
    color: #fff;
    padding: 16px 36px;
    border-radius: 12px;
    font-size: 17px;
    font-weight: 700;
    text-decoration: none;
    transition: all 0.2s;
    box-shadow: 0 8px 24px rgba(79,70,229,0.3);
  }
  .alt-cta:hover {
    background: #4338ca;
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(79,70,229,0.4);
  }

  .alt-section {
    padding: 80px 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .alt-section-title {
    font-size: 2.5rem;
    font-weight: 800;
    text-align: center;
    margin-bottom: 60px;
    color: #f1f5f9;
    letter-spacing: -0.02em;
  }

  .alt-comparison {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    align-items: center;
  }
  @media (max-width: 900px) {
    .alt-comparison { grid-template-columns: 1fr; gap: 48px; }
  }

  .alt-card {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 24px;
    padding: 40px;
    position: relative;
    overflow: hidden;
  }

  .alt-card.rdrto {
    background: rgba(99,102,241,0.04);
    border-color: rgba(99,102,241,0.2);
    box-shadow: 0 24px 80px rgba(0,0,0,0.5);
  }
  .alt-card.rdrto::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 4px;
    background: linear-gradient(90deg, #6366f1, #a855f7);
  }

  .alt-card-title {
    font-size: 2rem;
    font-weight: 900;
    margin: 0 0 32px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .alt-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .alt-list-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    font-size: 1.1rem;
    color: #cbd5e1;
    line-height: 1.5;
  }
  .alt-list-item.pro-con { color: #94a3b8; }
  
  .alt-icon { flex-shrink: 0; margin-top: 2px; fontSize: 1.2rem; }

  /* Feature highlight grid */
  .alt-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-top: 40px;
  }
  @media (max-width: 900px) {
    .alt-features { grid-template-columns: 1fr; }
  }

  .alt-feature-box {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 16px;
    padding: 32px 24px;
    text-align: center;
  }
  .alt-feature-icon {
    font-size: 2.5rem;
    margin-bottom: 20px;
  }
  .alt-feature-box h3 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #f1f5f9;
    margin: 0 0 12px;
  }
  .alt-feature-box p {
    font-size: 15px;
    color: #94a3b8;
    line-height: 1.6;
    margin: 0;
  }

  .alt-bottom-cta {
    text-align: center;
    padding: 100px 24px;
    background: linear-gradient(0deg, rgba(99,102,241,0.05) 0%, rgba(6,10,20,0) 100%);
  }
`;

const BitlyAlternative = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="alt-page">
      <Helmet>
        <title>Best Bitly Alternative in 2026 | Free Custom URL Shortener | RDRTO</title>
        <meta name="description" content="Looking for a free Bitly alternative? RDRTO offers more free links, advanced analytics, self-healing routing, and better pricing. Start shortening today." />
        <meta name="keywords" content="bitly alternative, free url shortener, custom link shortener, rebrandly alternative" />
      </Helmet>

      <style>{STYLES}</style>

      {/* Hero Section */}
      <section className="alt-hero">
        <span className="alt-badge">The Smarter Choice</span>
        <h1 className="alt-title">The Best <span className="alt-title-highlight">Bitly Alternative</span><br />for Modern Marketers</h1>
        <p className="alt-subtitle">
          Tired of Bitly's restrictive limits and high pricing? RDRTO gives you a generous free tier,
          self-healing links that never die, and deeper analytics for a fraction of the cost.
        </p>
        <Link to="/signup" className="alt-cta">
          Create Your First Short Link <span>→</span>
        </Link>
        <p style={{ marginTop: 16, fontSize: 13, color: '#64748b' }}>No credit card required for the free plan.</p>
      </section>

      {/* Comparison Section */}
      <section className="alt-section">
        <h2 className="alt-section-title">Why switch from Bitly to RDRTO?</h2>

        <div className="alt-comparison">
          {/* Bitly Card */}
          <div className="alt-card">
            <h3 className="alt-card-title" style={{ color: '#f43f5e' }}>Bitly</h3>
            <ul className="alt-list">
              <li className="alt-list-item pro-con">
                <span className="alt-icon">❌</span>
                <div><strong>Very low limits:</strong> The free tier is extremely restrictive for growing businesses.</div>
              </li>
              <li className="alt-list-item pro-con">
                <span className="alt-icon">❌</span>
                <div><strong>Expensive plans:</strong> Monthly costs scale aggressively once you need a custom domain.</div>
              </li>
              <li className="alt-list-item pro-con">
                <span className="alt-icon">❌</span>
                <div><strong>Dead links:</strong> If your destination URL breaks, your visitors hit a 404 page, losing you traffic and money.</div>
              </li>
              <li className="alt-list-item pro-con">
                <span className="alt-icon">❌</span>
                <div><strong>Basic analytics:</strong> High-level data that lacks deep geographic and hourly insights on lower tiers.</div>
              </li>
            </ul>
          </div>

          {/* RDRTO Card */}
          <div className="alt-card rdrto">
            <h3 className="alt-card-title" style={{ color: '#818cf8' }}>RDRTO</h3>
            <ul className="alt-list">
              <li className="alt-list-item">
                <span className="alt-icon">✅</span>
                <div><strong>Generous Free Tier:</strong> Start instantly with UP TO 20 free links and detailed click tracking.</div>
              </li>
              <li className="alt-list-item">
                <span className="alt-icon">✅</span>
                <div><strong>Fair Pricing:</strong> Affordable Pro and Business plans designed for creators and startups, not enterprises.</div>
              </li>
              <li className="alt-list-item">
                <span className="alt-icon">✅</span>
                <div><strong>🏥 Self-Healing Links:</strong> The only shortener that automatically routes traffic to a backup URL if your main site goes down.</div>
              </li>
              <li className="alt-list-item">
                <span className="alt-icon">✅</span>
                <div><strong>Link Intelligence Score:</strong> Built-in safety and performance analysis for every link you share.</div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="alt-section" style={{ paddingTop: 0 }}>
        <h2 className="alt-section-title">Features built for performance</h2>
        <div className="alt-features">
          <div className="alt-feature-box">
            <div className="alt-feature-icon">🏥</div>
            <h3>Self-Healing Routing</h3>
            <p>Never lose a click to a 404 error again. RDRTO continuously monitors your destination URLs and instantly redirects traffic to a pre-set backup if they fail.</p>
          </div>
          <div className="alt-feature-box">
            <div className="alt-feature-icon">🛡️</div>
            <h3>Intelligence Score</h3>
            <p>We analyze the safety, speed, and reliability of your long URLs, giving you the confidence to share links that won't get flagged as spam.</p>
          </div>
          <div className="alt-feature-box">
            <div className="alt-feature-icon">📊</div>
            <h3>Real-Time Analytics</h3>
            <p>Track exactly when and where your clicks are coming from with gorgeous geographic maps, hourly breakdowns, and device data.</p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="alt-bottom-cta">
        <h2 className="alt-section-title" style={{ marginBottom: 24 }}>Ready to make the switch?</h2>
        <p className="alt-subtitle" style={{ color: '#94a3b8' }}>
          Join the marketers and creators who have upgraded their link management.
        </p>
        <Link to="/signup" className="alt-cta" style={{ marginTop: 16 }}>
          Start for free today <span>→</span>
        </Link>
      </section>
    </div>
  );
};

export default BitlyAlternative;
